// @flow
import React, {useContext, useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ArchiverClientApi from "../../API/archiverClientApi";
import ErrorPopup from "../../GUI_COMMON/COMPONENTS/ErrorPopup";
import CheckInput from "../../GUI_COMMON/COMPONENTS/CheckInput";
import DigitInput from "../../GUI_COMMON/COMPONENTS/DigitInput";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import {StoreContext} from "../../GUI_COMMON/SUPPORT/store";
import {gotMagTypes} from "../../GUI_COMMON/ACTIONS/commonClientActions";

const MagTypesAdminPage = () => {

    const [,dispatch] = useContext(StoreContext);

    const [curMagTypeDetails, setCurMagTypeDetails] = useState(getDefaultMagTypeDetails());
    const [fullMagTypesList, setFullMagTypesList] = useState([]);
    const [sampleTypesList, setSampleTypesList] = useState([]);
    
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    
    const history = useHistory();
    const {magTypeId} = useParams();          // set in the url
    
    useEffect(() => {
        console.debug("refreshing magTypes list");
        getSampleTypesList(); // this also gets the magTypesList
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(fullMagTypesList && fullMagTypesList.length>0) {
            doRealListItemClicked(magTypeId?Number(magTypeId):null);  // preselect if the url has something in it.
        }
    },[fullMagTypesList]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    function getDefaultMagTypeDetails():{} {
        return(
            {
                id: null,
                description: "",
                holdsSampleType: 0,
                isIndexed: false,
                dupsAllowedInSameMag: false,
                numColumns: 4,
                numRows: 50,
                numMagsPerBox: 8,
                columnNamesAreAlpha: true,
                rowNamesAreAlpha: false,
                canBeDumpedAfterDays: 0,
            }
        );
    }
    
    function getMagTypesList() {
        setIsLoading(true);
        ArchiverClientApi.sendMagTypesRequest() // request ALL magTypes 
        .then( (magTypes) => {
            sortMagTypesList(magTypes);
            setFullMagTypesList(magTypes??[]);
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }

    function sortMagTypesList(magTypesList) {
        if(magTypesList) {
            magTypesList.sort((a, b) => {
                if (a.description < b.description) return -1;
                if (a.description > b.description) return 1;
                return 0;
            });
        }
    }
    
    function getSampleTypesList() {
        setIsLoading(true);
        ArchiverClientApi.sendSampleTypesRequest() // request ALL magTypes 
        .then( (sampleTypes) => {
            setSampleTypesList(sampleTypes??[]);
            getMagTypesList();
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function handleCancelClicked(e) {
        e.preventDefault();
        e.stopPropagation();
        
        confirmClearDirty();
    }
    
    function confirmClearDirty() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetMagTypeDetailsShown();
                });
        }
        else {
            resetMagTypeDetailsShown(); // just clear what's there
        }
    }
    
    function handleDeleteMagTypeClicked(e) {
        e.preventDefault();
        e.stopPropagation();
        
        Popup.showConfirm("Delete Mag Type?",`<p>This will attempt to remove '${curMagTypeDetails.description}'.</p>`+
                                " If you have any magazines or profiles currently using this type, "+
                                " you will be unable to remove this mag type until those dependancies are removed.</p>"+
                                "<p>Are you sure you want to do this?</p>","Yes, Delete it",null, 
            () => {
               doMagTypeRemove(curMagTypeDetails.description,curMagTypeDetails.id);
            });
    }
   
    function doMagTypeRemove(magTypeName, magTypeId) {
        setIsRemoving(true);
        ArchiverClientApi.sendMagTypeRemove(magTypeId)
        .then(() => {
            Popup.showInfo(`MagType '${magTypeName}' removed`);
            removeFromFullMagTypeList(magTypeId);
            resetMagTypeDetailsShown();
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally(() => {
            setIsRemoving(false);
        });
    }
    
    function handleSaveClicked(e,action) {
        e.preventDefault();
        e.stopPropagation();
        
        if(!checkForValid()) {
            return;
        }
       
        setIsSaving(true);
        ArchiverClientApi.sendMagTypeUpdate(curMagTypeDetails)
        .then( (savedMagTypeInfo) => {
            Popup.showInfo(`MagType '${curMagTypeDetails.description}' Saved`);
            setIsDirty(false);
            if(action==="ADD") {
                addToFullMagTypeList(savedMagTypeInfo);
                resetMagTypeDetailsShown();
            }
            else if(action==="UPDATE") {
                updateExistingMagType(savedMagTypeInfo);
            }
            // clear out the stored magTypes so ROs and stuff get the new list when they are re-visited
            dispatch(gotMagTypes([]));
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
           setIsSaving(false); 
        });
    }
    
    function checkForValid():boolean {
        let isValid=true;
        let notValidMsg="";
        if(curMagTypeDetails.description==null || curMagTypeDetails.description.trim()==="") {
            isValid=false;
            notValidMsg="Description should not be blank";
        }
        
        if(curMagTypeDetails.numColumns<=0 || curMagTypeDetails.numRows<=0) {
            isValid=false;
            notValidMsg="Columns and Rows must both be greater than zero";
        }
        if(curMagTypeDetails.numMagsPerBox<0) {
            isValid=false;
            notValidMsg="Mags per box must be zero or greater";
        }
       
        if(!isValid) {
            ErrorPopup.showError("Validation Problem",notValidMsg);
            return false;
        }
        return true;

    }
    
    function addToFullMagTypeList(userDetails) {
        const curList = [...fullMagTypesList];
        curList.push(userDetails);
        curList.sort( (a,b) => {
            if(a.description < b.description) return -1;
            if(a.description > b.description) return 1;
            return 0;
        });
        setFullMagTypesList(curList);
    }

    function removeFromFullMagTypeList(userId) {
        const curList = fullMagTypesList.filter( item => item.id !== userId);
        setFullMagTypesList(curList);
    }
    
    function updateExistingMagType(magTypeDetails) {
        const curList = [...fullMagTypesList];
        let thisUserIndex = curList.findIndex(item => item.id===magTypeDetails.id);
        curList[thisUserIndex]=magTypeDetails;
        setFullMagTypesList(curList);
    }
    
    function onChange(e) {
        const thing = e.target.name;
        const editInfo = {...curMagTypeDetails};
        if(e.target.type==="checkbox") { // this is a checkbox
            
            if(thing==="dupsAllowedInSameMag") { // set opposite 
                editInfo[thing] = !e.target.checked; // dupsAllowedInSameMag updated 
            }
            else {
                editInfo[thing] = e.target.checked;
            }
        }
        else {
            let value = e.target.value;
            if(thing==="canBeDumpedAfterDays" || thing==="numRows" || thing==="numColumns" || 
                thing==="numMagsPerBox" || thing==="holdsSampleType") {  // number
                value = Number(value);
            }
            if(thing==="desc") { // rename
                editInfo["description"] = value;
            }
            else {
                editInfo[thing] = value;
            }
        }
        setCurMagTypeDetails(editInfo);
        setIsDirty(true);
    }
    
    function showMagTypesList(listContents) {

        if(isLoading) {
            return <div className="pr-4 w-30 li-border-right-light">...loading...</div>;
        }
        
        let resultList = listContents.map((item,index) => {
                let extraClass = "";
                if(curMagTypeDetails?.id===item.id) {
                    extraClass += " li-bg-light";
                }
                return <li key={index} className={"list-group-item li-pointer "+(item.isEnabled===false?"li-fg-slightly-muted":"")
                +extraClass}
                    onClick={() => handleListItemClicked(item)}>{item.description}</li>;
            }
        );
        if(!resultList || resultList.length===0) {
            resultList = "no magTypes found";
        }

        return (
            <div className="pr-4 w-30 li-border-right-light">
                <h4>Mag Types</h4>
                <div className="li-scrollable-at-500 ">
                    <ul className="list-group-flush li-list-group-w-hover pl-0">
                        {resultList}
                    </ul>
                    <div className="li-border-line-light"/>
                </div>
                <LIButton small primary label="" bgClass={isRemoving?"m-3":"fa fa-trash ml-3 my-3"} disabled={curMagTypeDetails?.id===null}
                          onClick={handleDeleteMagTypeClicked} showBusy={isRemoving}/>
                <LIButton small primary label="" bgClass={isRemoving?"":"fa fa-plus m-3"} onClick={confirmClearDirty} showBusy={false}/>
            </div>
        );
    }

    function dumpHoldsSampleTypeOptionsList() {
        if(sampleTypesList && sampleTypesList.length>0) {
            return sampleTypesList.map(item => <option key={item.id} value={item.id}>{item.name}</option>);
        }
    }

    function dumpSampleTypeListSection() {
        return(
            <>
                <label htmlFor="type" className="li-form-label mr-3">Holds Sample Type</label>
                <select onChange={onChange} id="holdsSampleType" name="holdsSampleType" value={curMagTypeDetails.holdsSampleType??0}
                        className="li-form-field no-width align-self-end li-border-dark" required={true}>
                    <option value={0} disabled>(select a type)</option>
                    {dumpHoldsSampleTypeOptionsList()}
                </select>
                <LIButton label="" small primary faIcon="fa fa-external-link" bgClass="ml-3" onClick={handleGoToSampleTypeClicked}/>
                <small className="form-text text-muted">* determines what type of sample can go into this mag</small>
            </>
        );
    }

    function handleGoToSampleTypeClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.push("/admin/sample_types/"+curMagTypeDetails.holdsSampleType);
                });
        }
        else {
            history.push("/admin/sample_types/"+curMagTypeDetails.holdsSampleType);
        }
    }

    function handleListItemClicked(item) {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetMagTypeDetailsShown();
                    doRealListItemClicked(item?.id);
                });
        }
        else {
            doRealListItemClicked(item?.id);
        }
    }
    
    function doRealListItemClicked(itemId) {
        const magType = fullMagTypesList.find(i => i.id===itemId);
        if(!magType) {
            resetMagTypeDetailsShown();
        }
        else {
            setCurMagTypeDetails(magType);
            updateUrlLink(`/admin/mag_types/${magType.id}`);
        }
    }
    
    function updateUrlLink(toThis) {
        history.replace(toThis);
    }
    
    function resetMagTypeDetailsShown() {
        setCurMagTypeDetails(getDefaultMagTypeDetails());
        setIsDirty(false);
        updateUrlLink("/admin/mag_types"); // blank

        // focus the first input field
        const nameEl:HTMLInputElement = document.getElementsByName("desc")[0];
        if(nameEl) {
            nameEl.focus();
        }
    }
    
    function handleBackClicked() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    history.goBack();
                });
        }
        else {
            history.goBack();
        }
        
    }
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="li-header-row">
                <BackButton onClick={handleBackClicked}/>
                <h1 className="ml-4 d-inline-block align-middle">Mag Type Administration</h1>
            </div>
            
            <div className="row">
                {/* LEFT SIDE USER LIST */}
                {showMagTypesList(fullMagTypesList)}

                {/* RIGHT SIDE FORM */}
                <div className="pl-4 w-70">
                    <div className="li-fg-slightly-muted li-font-small text-right">[Id:{curMagTypeDetails?.id??"-"}]</div>

                    <div className="row m-0 p-0 justify-content-between">
                        <div className="col p-0">
                            <TextInput name="desc" label="Description" onChange={(e) => onChange(e)}
                                       placeholder="" textLeft
                                       value={curMagTypeDetails?.description ?? ""}
                                       required/>
                            <DigitInput onChange={onChange} minLength={1} maxLength={3} oneRow
                                        label="# Columns" name="numColumns"
                                        value={curMagTypeDetails?.numColumns ?? 4}
                                        inputWidthPels={70}
                                        helpText="Number of columns (or sections) in the magazine grid"/>
                            <DigitInput onChange={onChange} minLength={1} maxLength={3} oneRow
                                        label="# Rows" name="numRows" value={curMagTypeDetails?.numRows ?? 50}
                                        inputWidthPels={70}
                                        helpText="Number of rows in a single column"/>
                            <DigitInput onChange={onChange} minLength={1} maxLength={2} oneRow
                                        label="# Mags Per Box" name="numMagsPerBox"
                                        value={curMagTypeDetails?.numMagsPerBox ?? 8}
                                        inputWidthPels={70}
                                        helpText="Number of these mags go into a single box (use 0 if this mag type is unboxed)"/>
                            

                        </div>
                        <div className="col">
                            <div className="mt-4 card card-body li-bg-light text-right">
                                <div className="mt-3">
                                    <CheckInput label="Is indexed?" name="isIndexed"
                                                checked={curMagTypeDetails.isIndexed}
                                                onChanged={(e) => onChange(e)}
                                                helpLink="admin/general_mags.html#isIndexed"/>
                                </div>
                                <div className="mt-3">
                                    <CheckInput label="Duplicates auto-merged if in the same mag?" name="dupsAllowedInSameMag"
                                                checked={curMagTypeDetails.isIndexed && !curMagTypeDetails.dupsAllowedInSameMag}
                                                labelWidthPels="230" disabled={!curMagTypeDetails.isIndexed}
                                                onChanged={(e) => onChange(e)}
                                                helpLink="admin/general_mags.html#dupsAllowedInSameMag"/>
                                </div>
                                {/* as of 250124 -pott, we are using the section ALPHA as the position in the mag */}
                               {/*  <div className="mt-3">
                                    <CheckInput label="Columns are alpha?" name="columnNamesAreAlpha"
                                                checked={curMagTypeDetails.columnNamesAreAlpha}
                                                onChanged={(e) => onChange(e)}
                                                helpLink="admin/general_mags.html#namesAreAlpha"/>
                                </div> */}
                                {/*<div className="mt-3">*/}
                                {/*    <CheckInput label="Rows are alpha?" name="rowNamesAreAlpha"*/}
                                {/*                checked={curMagTypeDetails.rowNamesAreAlpha}*/}
                                {/*                onChanged={(e) => onChange(e)}*/}
                                {/*                helpLink="admin/general_mags.html#namesAreAlpha"/>*/}
                                {/*</div>*/}
                            </div>
                        </div>
                    </div>
                    <DigitInput onChange={onChange} minLength={1} maxLength={4} oneRow
                                label="Can Be Discarded After (days)" name="canBeDumpedAfterDays"
                                value={curMagTypeDetails?.canBeDumpedAfterDays ?? 0}
                                inputWidthPels={70}
                                helpText="# of days to keep this mag before it can be disarded (use 0 days to disable)"/>
                    {dumpSampleTypeListSection()}

                    {/* BUTTON ROW */}
                    <div className="text-center my-5">
                        <LIButton name="cancel" label={isDirty ? "Cancel" : "Clear"} bgClass="mr-4" primary
                                  onClick={handleCancelClicked}/>
                        <LIButton name="save" label={curMagTypeDetails?.id === null ? "ADD" : "UPDATE"}
                                  secondary disabled={!isDirty || isSaving}
                                  onClick={(e) => handleSaveClicked(e, curMagTypeDetails?.id === null ? "ADD" : "UPDATE")}
                                  showBusy={isSaving}/>
                    </div>
                </div>
                </div>
        </div>
    );
}

export default MagTypesAdminPage;